import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import type { Page } from "../../api";
import { FlagChip } from "../../chips";
import { readShared } from "../../filters";
import { ItemLink, OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import type { PageProps } from "../index";
import { f2, MUTED_ROW, PAGE_SIZE, PagedTable, PageHead, RatingBar, RefLegend, sortParams, usePage, useTableSort, type TableSort } from "../kit";
import { pageExtra } from "../params";

export interface FeatureRankingRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  funnel: string;
  phase: string | null;
  dropped: boolean;
  feature: string | null;
  rating: number | null;
  criteria_count: number | null;
  low_evidence: boolean | null;
  not_scored: boolean;
  /** The true rank by rating from the API, so # stays right whatever the table is sorted by. */
  rank_n?: number | null;
  criteria_total: number;
}

/** Rank on the whole list (not just this page); an option with no rating has none. */
export function positionOf(row: FeatureRankingRow, index: number, page: number): string {
  if (row.rating == null) return "–";
  return String(row.rank_n ?? (page - 1) * PAGE_SIZE + index + 1);
}

export function featureRankingColumns(page: number): ColumnDef<FeatureRankingRow, unknown>[] {
  return [
    { id: "rank", header: "#", cell: ({ row }) => <span className="text-ks-grey-1">{positionOf(row.original, row.index, page)}</span> },
    {
      id: "option",
      header: "Option",
      meta: { sortable: true },
      cell: ({ row }) => (
        <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from="feature_ranking">
          {row.original.option_name}
        </OptionLink>
      ),
    },
    {
      id: "item",
      header: "Item",
      meta: { sortable: true },
      cell: ({ row }) => (
        <ItemLink itemId={row.original.item_id} from="feature_ranking">
          {row.original.item_name}
        </ItemLink>
      ),
    },
    { id: "rating", header: "Rating", meta: { align: "right", sortable: true }, cell: ({ row }) => f2(row.original.rating) },
    { id: "bar", header: () => <RefLegend />, cell: ({ row }) => <RatingBar value={row.original.rating} /> },
    { id: "criteria", header: "Criteria count", meta: { align: "right", sortable: true }, cell: ({ row }) => row.original.criteria_count ?? 0 },
    {
      id: "flags",
      header: "Flags",
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          {row.original.low_evidence && (row.original.criteria_count ?? 0) > 0 ? <FlagChip kind="low_evidence" /> : null}
          {row.original.not_scored ? <FlagChip kind="not_scored" /> : null}
        </span>
      ),
    },
  ];
}

export function FeatureRankingBody({ data, page, onPage, sort = null, onSort }: {
  data: Page<FeatureRankingRow>; page: number; onPage: (next: number) => void; sort?: TableSort; onSort?: (id: string) => void;
}) {
  return (
    <PagedTable<FeatureRankingRow>
      columns={featureRankingColumns(page)}
      data={data}
      page={page}
      onPage={onPage}
      caption="Options ranked by rating across items"
      sort={sort}
      onSort={onSort}
      rowClassName={(row) => (row.dropped ? MUTED_ROW : undefined)}
    />
  );
}

export function FeatureRankingPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const [page, setPage] = usePage();
  const [sort, onSort] = useTableSort();
  const oneItem = readShared(search).item !== null;
  return (
    <TabPage<Page<FeatureRankingRow>>
      tab={tab}
      areaName={areaName}
      extra={{ ...pageExtra(tab.tabKey, search), ...sortParams(sort), limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }}
      isEmpty={(data) => data.total === 0}
      head={(data) => {
        const first = data?.items[0];
        return (
          <PageHead
            title={tab.label}
            sub={data ? (oneItem ? "One item" : "All items in scope — cross-item ranking") : undefined}
            kpi={first ? { label: "Total criteria", value: first.criteria_total } : null}
          />
        );
      }}
    >
      {(data) => <FeatureRankingBody data={data} page={page} onPage={setPage} sort={sort} onSort={onSort} />}
    </TabPage>
  );
}
